import { createReducer } from '@reduxjs/toolkit';
import {
	ChatRef,
	CreditWallet,
	DEFAULT_USER_SETTINGS,
	DesignSystem,
	DesignSystemVersion,
	DropShadowMode,
	DsChat,
	DsFileChange,
	DsMode,
	DsVersionRef,
	Frame,
	OrganizationCreditWallet,
	ReferralInfo,
	Scene,
	SceneChat,
	SceneComment,
	SceneDsLink,
	Subscription,
	Tier,
	User,
	UserSettings,
} from 'dragonmouse-shared/interfaces';
import { Style } from 'dragonmouse-shared/style';
import {
	clearActiveDesignSystem,
	clearActiveScene,
	LoadingState,
	optimisticallyUpdateDesignSystems,
	optimisticallyUpdateDsChats,
	optimisticallyUpdateFrames,
	optimisticallyUpdateSceneChats,
	optimisticallyUpdateSceneComments,
	optimisticallyUpdateScenes,
	optimisticallyUpdateUser,
	OrganizationWithRole,
	resolveOptimisticallyUpdatedDesignSystems,
	resolveOptimisticallyUpdatedDsChats,
	resolveOptimisticallyUpdatedFrames,
	resolveOptimisticallyUpdatedSceneChats,
	resolveOptimisticallyUpdatedSceneComments,
	resolveOptimisticallyUpdatedScenes,
	resolveOptimisticallyUpdatedUser,
	setDesignSystemLoadError,
	setDsCanWrite,
	setDsHasDraft,
	setDsMigrating,
	setDsPreparing,
	setDsTitle,
	setFrameCommentAuthors,
	setLoadingState,
	setSceneLoadError,
	updateActiveDesignSystemVersion,
	updateActiveDsChat,
	updateActiveSceneChat,
	updateActiveSceneDesignSystemModes,
	updateActiveSceneDsLink,
	updateCreditWallet,
	updateDesignSystems,
	updateDraftChanges,
	updateDsChatRefs,
	updateDsVersionRefs,
	updateFigmaConnected,
	updateFrames,
	updateGithubConnected,
	updateGuestDesignSystems,
	updateGuestFrames,
	updateGuestScenes,
	updateOrganizationCreditWallets,
	updateOrganizationDesignSystemIds,
	updateOrganizations,
	updateOrganizationSceneIds,
	updateReferralInfo,
	updateSceneChatRefs,
	updateSceneComments,
	updateScenes,
	updateSubscription,
	updateUser,
	updateUserDesignSystemIds,
	updateUserSceneIds,
} from './actions';

// In-flight server writes for one entity. `id` is the latest writeId; `mergedIds`
// holds absorbed writeIds from collapsed writes (each still needs its own resolution).
export type OptimisticWrite<T> = {
	id: string;
	mergedIds?: string[];
	operation: `insert` | `update` | `delete`;
	value: Partial<T> & { id: string };
};

// definition of our store's types
export type Store = {
	scenes: Scene[];
	guestScenes: Scene[];
	designSystems: DesignSystem[];
	guestDesignSystems: DesignSystem[];
	frames: Frame[];
	guestFrames: Frame[];
	activeSceneChat: SceneChat | null;
	user: User | null;
	frameCommentAuthors: User[];
	subscription: Subscription | null;
	creditWallet: CreditWallet | null;
	organizations: OrganizationWithRole[];
	organizationSceneIds: Record<string, string[]>;
	organizationDesignSystemIds: Record<string, string[]>;
	organizationCreditWallets: Record<string, OrganizationCreditWallet>;
	userSceneIds: string[];
	userDesignSystemIds: string[];
	figmaConnected: boolean;
	githubConnected: boolean;
	referralInfo: ReferralInfo | null;
	activeDesignSystemVersion: DesignSystemVersion | null;
	dsVersionRefs: DsVersionRef[];
	dsPreparing: boolean;
	dsMigrating: boolean;
	dsHasDraft: boolean;
	dsTitle: string;
	dsCanWrite: boolean;
	draftChanges: DsFileChange[] | null;
	dsChatRefs: ChatRef[];
	activeDsChat: DsChat | null;
	sceneChatRefs: ChatRef[];
	sceneComments: SceneComment[];
	activeSceneDsLink: SceneDsLink | null;
	activeSceneDesignSystemModes: DsMode[] | null;
	optimisticSceneWrites: OptimisticWrite<Scene>[];
	optimisticDesignSystemWrites: OptimisticWrite<DesignSystem>[];
	optimisticFrameWrites: OptimisticWrite<Frame>[];
	optimisticSceneChatWrites: OptimisticWrite<SceneChat>[];
	optimisticSceneCommentWrites: OptimisticWrite<SceneComment>[];
	optimisticDsChatWrites: OptimisticWrite<DsChat>[];
	optimisticUserWrites: OptimisticWrite<User>[];
	loading: LoadingState;
	sceneLoadError: string | null;
	designSystemLoadError: string | null;
};

// initialize our slice of the store to its default value
const initialState: Store = {
	scenes: [],
	guestScenes: [],
	designSystems: [],
	guestDesignSystems: [],
	frames: [],
	guestFrames: [],
	activeSceneChat: null,
	user: null,
	frameCommentAuthors: [],
	subscription: null,
	creditWallet: null,
	organizations: [],
	organizationSceneIds: {},
	organizationDesignSystemIds: {},
	organizationCreditWallets: {},
	userSceneIds: [],
	userDesignSystemIds: [],
	figmaConnected: false,
	githubConnected: false,
	referralInfo: null,
	activeDesignSystemVersion: null,
	dsVersionRefs: [],
	dsPreparing: false,
	dsMigrating: false,
	dsHasDraft: false,
	dsTitle: '',
	dsCanWrite: false,
	draftChanges: null,
	dsChatRefs: [],
	activeDsChat: null,
	sceneChatRefs: [],
	sceneComments: [],
	activeSceneDsLink: null,
	activeSceneDesignSystemModes: null,
	optimisticSceneWrites: [],
	optimisticDesignSystemWrites: [],
	optimisticFrameWrites: [],
	optimisticSceneChatWrites: [],
	optimisticSceneCommentWrites: [],
	optimisticDsChatWrites: [],
	optimisticUserWrites: [],
	loading: {
		profile: 'never-loaded',
		scenes: 'never-loaded',
		activeDesignSystemVersion: 'never-loaded',
		activeDsChat: 'never-loaded',
		activeScene: {
			frames: 'never-loaded',
			chats: 'never-loaded',
		},
		guest: {
			scenes: 'never-loaded',
			frames: 'never-loaded',
			designSystems: 'never-loaded',
		},
	},
	sceneLoadError: null,
	designSystemLoadError: null,
};

export const DEFAULT_CANVAS_COLOR: string = '#555555';
export const DEFAULT_CANVAS_DROP_SHADOW_MODE: DropShadowMode = DropShadowMode.MEDIUM;

// Collapses same-entity update writes. Values are shallow-merged, except `metadata`
// which is deep-merged one level. Older ids preserved in mergedIds so each server
// call still gets resolved. Inserts/deletes aren't merged (ambiguous).
function mergeOptimisticWrites<T>(
	existingWrites: OptimisticWrite<T>[],
	newWrites: OptimisticWrite<T>[]
): OptimisticWrite<T>[] {
	const result = [...existingWrites];
	for (const incoming of newWrites) {
		const mergeIdx =
			incoming.operation === 'update'
				? result.findIndex((w) => w.operation === 'update' && w.value.id === incoming.value.id)
				: -1;
		if (mergeIdx >= 0) {
			const existing = result[mergeIdx];
			const absorbedIds = [
				...(existing.mergedIds ?? []),
				existing.id,
				...(incoming.mergedIds ?? []),
			];
			const existingMeta = (existing.value as { metadata?: unknown }).metadata;
			const incomingMeta = (incoming.value as { metadata?: unknown }).metadata;
			const mergedValue =
				existingMeta &&
				incomingMeta &&
				typeof existingMeta === 'object' &&
				typeof incomingMeta === 'object'
					? {
							...existing.value,
							...incoming.value,
							metadata: { ...existingMeta, ...incomingMeta },
					  }
					: { ...existing.value, ...incoming.value };
			result[mergeIdx] = {
				id: incoming.id,
				mergedIds: absorbedIds,
				operation: 'update',
				value: mergedValue as Partial<T> & { id: string },
			};
		} else {
			result.push(incoming);
		}
	}
	return result;
}

// Removes resolved writeIds from each write (both primary and mergedIds). If a
// write's ids are all resolved, it's dropped entirely. If the primary id resolves
// but merged ids remain, one is promoted to primary. This replaces the old
// filter-based resolution which couldn't express partial resolution of a
// merged write.
function trimResolvedWrites<T>(
	writes: OptimisticWrite<T>[],
	resolvedIds: string[]
): OptimisticWrite<T>[] {
	if (resolvedIds.length === 0) return writes;
	const resolved = new Set(resolvedIds);
	const result: OptimisticWrite<T>[] = [];
	for (const write of writes) {
		const allIds = [write.id, ...(write.mergedIds ?? [])];
		const remaining = allIds.filter((id) => !resolved.has(id));
		if (remaining.length === 0) continue;
		if (remaining.length === allIds.length) {
			result.push(write);
			continue;
		}
		result.push({
			...write,
			id: remaining[0],
			mergedIds: remaining.length > 1 ? remaining.slice(1) : undefined,
		});
	}
	return result;
}

const reducer = createReducer(initialState, (builder) => {
	//------------------------------------------------------------------------------------------------
	// scenes
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateScenes, (state, action) => {
		state.scenes = action.payload;
	});

	builder.addCase(updateGuestScenes, (state, action) => {
		state.guestScenes = action.payload;
	});

	builder.addCase(optimisticallyUpdateScenes, (state, action) => {
		state.optimisticSceneWrites = mergeOptimisticWrites(
			state.optimisticSceneWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedScenes, (state, action) => {
		state.optimisticSceneWrites = trimResolvedWrites(state.optimisticSceneWrites, action.payload);
	});

	builder.addCase(setSceneLoadError, (state, action) => {
		state.sceneLoadError = action.payload;
	});

	builder.addCase(setDesignSystemLoadError, (state, action) => {
		state.designSystemLoadError = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// design systems
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateDesignSystems, (state, action) => {
		state.designSystems = action.payload;
	});

	builder.addCase(updateGuestDesignSystems, (state, action) => {
		state.guestDesignSystems = action.payload;
	});

	builder.addCase(optimisticallyUpdateDesignSystems, (state, action) => {
		state.optimisticDesignSystemWrites = mergeOptimisticWrites(
			state.optimisticDesignSystemWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedDesignSystems, (state, action) => {
		state.optimisticDesignSystemWrites = trimResolvedWrites(
			state.optimisticDesignSystemWrites,
			action.payload
		);
	});

	builder.addCase(updateActiveDesignSystemVersion, (state, action) => {
		state.activeDesignSystemVersion = action.payload;
	});

	builder.addCase(updateDsVersionRefs, (state, action) => {
		state.dsVersionRefs = action.payload;
	});

	builder.addCase(setDsPreparing, (state, action) => {
		state.dsPreparing = action.payload;
	});

	builder.addCase(setDsMigrating, (state, action) => {
		state.dsMigrating = action.payload;
	});

	builder.addCase(setDsHasDraft, (state, action) => {
		state.dsHasDraft = action.payload;
	});

	builder.addCase(setDsTitle, (state, action) => {
		state.dsTitle = action.payload;
	});

	builder.addCase(setDsCanWrite, (state, action) => {
		state.dsCanWrite = action.payload;
	});

	builder.addCase(updateDraftChanges, (state, action) => {
		state.draftChanges = action.payload;
	});

	builder.addCase(updateDsChatRefs, (state, action) => {
		state.dsChatRefs = action.payload;
	});

	builder.addCase(updateActiveDsChat, (state, action) => {
		state.activeDsChat = action.payload;
	});

	builder.addCase(optimisticallyUpdateDsChats, (state, action) => {
		state.optimisticDsChatWrites = mergeOptimisticWrites(
			state.optimisticDsChatWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedDsChats, (state, action) => {
		state.optimisticDsChatWrites = trimResolvedWrites(state.optimisticDsChatWrites, action.payload);
	});

	builder.addCase(clearActiveScene, (state) => {
		state.frames = [];
		state.activeSceneChat = null;
		state.sceneChatRefs = [];
		state.sceneComments = [];
		state.optimisticSceneCommentWrites = [];
		state.activeSceneDsLink = null;
		state.activeSceneDesignSystemModes = null;
	});

	builder.addCase(clearActiveDesignSystem, (state) => {
		state.activeDesignSystemVersion = null;
		state.dsVersionRefs = [];
		state.dsPreparing = false;
		state.dsMigrating = false;
		state.dsHasDraft = false;
		state.dsTitle = '';
		state.dsCanWrite = false;
		state.draftChanges = null;
		state.dsChatRefs = [];
	});

	//------------------------------------------------------------------------------------------------
	// frames
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateFrames, (state, action) => {
		state.frames = action.payload;
	});

	builder.addCase(updateGuestFrames, (state, action) => {
		state.guestFrames = action.payload;
	});

	builder.addCase(optimisticallyUpdateFrames, (state, action) => {
		state.optimisticFrameWrites = mergeOptimisticWrites(
			state.optimisticFrameWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedFrames, (state, action) => {
		state.optimisticFrameWrites = trimResolvedWrites(state.optimisticFrameWrites, action.payload);
	});

	//------------------------------------------------------------------------------------------------
	// scene chats
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateActiveSceneChat, (state, action) => {
		state.activeSceneChat = action.payload;
	});

	builder.addCase(optimisticallyUpdateSceneChats, (state, action) => {
		state.optimisticSceneChatWrites = mergeOptimisticWrites(
			state.optimisticSceneChatWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedSceneChats, (state, action) => {
		state.optimisticSceneChatWrites = trimResolvedWrites(
			state.optimisticSceneChatWrites,
			action.payload
		);
	});

	builder.addCase(updateSceneChatRefs, (state, action) => {
		state.sceneChatRefs = action.payload;
	});

	builder.addCase(updateSceneComments, (state, action) => {
		state.sceneComments = action.payload;
	});

	builder.addCase(optimisticallyUpdateSceneComments, (state, action) => {
		state.optimisticSceneCommentWrites = mergeOptimisticWrites(
			state.optimisticSceneCommentWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedSceneComments, (state, action) => {
		state.optimisticSceneCommentWrites = trimResolvedWrites(
			state.optimisticSceneCommentWrites,
			action.payload
		);
	});

	builder.addCase(updateActiveSceneDsLink, (state, action) => {
		state.activeSceneDsLink = action.payload;
	});

	builder.addCase(updateActiveSceneDesignSystemModes, (state, action) => {
		state.activeSceneDesignSystemModes = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// user
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateUser, (state, action) => {
		state.user = action.payload;
	});

	builder.addCase(optimisticallyUpdateUser, (state, action) => {
		state.optimisticUserWrites = mergeOptimisticWrites(state.optimisticUserWrites, action.payload);
	});

	builder.addCase(resolveOptimisticallyUpdatedUser, (state, action) => {
		state.optimisticUserWrites = trimResolvedWrites(state.optimisticUserWrites, action.payload);
	});

	//------------------------------------------------------------------------------------------------
	// subscription
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateSubscription, (state, action) => {
		state.subscription = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// credit wallet
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateCreditWallet, (state, action) => {
		state.creditWallet = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// frame comment authors
	//------------------------------------------------------------------------------------------------

	builder.addCase(setFrameCommentAuthors, (state, action) => {
		state.frameCommentAuthors = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// organizations
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateOrganizations, (state, action) => {
		state.organizations = action.payload;
	});

	builder.addCase(updateOrganizationSceneIds, (state, action) => {
		state.organizationSceneIds = action.payload;
	});

	builder.addCase(updateOrganizationDesignSystemIds, (state, action) => {
		state.organizationDesignSystemIds = action.payload;
	});

	builder.addCase(updateOrganizationCreditWallets, (state, action) => {
		state.organizationCreditWallets = action.payload;
	});

	builder.addCase(updateUserSceneIds, (state, action) => {
		state.userSceneIds = action.payload;
	});

	builder.addCase(updateUserDesignSystemIds, (state, action) => {
		state.userDesignSystemIds = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// integrations
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateFigmaConnected, (state, action) => {
		state.figmaConnected = action.payload;
	});

	builder.addCase(updateGithubConnected, (state, action) => {
		state.githubConnected = action.payload;
	});

	builder.addCase(updateReferralInfo, (state, action) => {
		state.referralInfo = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// loading state
	//------------------------------------------------------------------------------------------------

	builder.addCase(setLoadingState, (state, action) => {
		const { resource, state: newState } = action.payload;

		// Handle nested paths like 'activeScene.frames'
		if (resource.includes('.')) {
			const [parent, child] = resource.split('.');
			if (parent === 'activeScene') {
				(state.loading.activeScene as any)[child] = newState;
			} else if (parent === 'guest') {
				(state.loading.guest as any)[child] = newState;
			}
		} else {
			// Handle top-level resources
			if (resource === 'profile') {
				state.loading.profile = newState;
			} else if (resource === 'scenes') {
				state.loading.scenes = newState;
			} else if (resource === 'activeDesignSystemVersion') {
				state.loading.activeDesignSystemVersion = newState;
			} else if (resource === 'activeDsChat') {
				state.loading.activeDsChat = newState;
			}
		}
	});
});

//------------------------------------------------------------------------------------------------
// selectors
//------------------------------------------------------------------------------------------------
export const selectActiveScene = (state: Store, activeSceneId: string | null): Scene | null => {
	if (!activeSceneId) return null;

	// Check regular scenes first (they take precedence over guest scenes)
	const scene = state.scenes.find((scene) => scene.id === activeSceneId) || null;
	if (scene) {
		// Apply optimistic writes to regular scenes
		const optimisticWrites = Object.values(state.optimisticSceneWrites);
		return optimisticWrites
			.filter((w) => w.value.id === activeSceneId)
			.reduce((scene: Scene | null, { operation, value }: OptimisticWrite<Scene>): Scene | null => {
				switch (operation) {
					case 'insert':
						return value as Scene;
					case 'update':
						return { ...scene, ...value } as Scene;
					case 'delete':
						return null;
					default:
						return scene;
				}
			}, scene);
	}

	// Fall back to guest scenes if not found in regular scenes
	// guest scenes are readonly so we don't need to check optimistic writes
	const guestScene = state.guestScenes.find((scene) => scene.id === activeSceneId) || null;
	return guestScene;
};

export const selectActiveStyle = (state: Store, activeSceneId: string | null): Style | null => {
	const scene = selectActiveScene(state, activeSceneId);
	if (!scene?.metadata?.styles?.length) return null;
	const currentStyle = scene.metadata?.currentStyle;
	if (currentStyle === null) return null;
	if (currentStyle >= scene.metadata.styles.length) return null;
	return scene.metadata.styles[currentStyle];
};

export const selectActiveSceneChat = (
	state: Store,
	expectedChatId?: string | null
): SceneChat | null => {
	const chatId = expectedChatId ?? state.activeSceneChat?.id ?? null;
	if (!chatId) return null;

	const sceneChat = state.activeSceneChat?.id === chatId ? state.activeSceneChat : null;
	const optimisticWrites = Object.values(state.optimisticSceneChatWrites);

	return optimisticWrites
		.filter((w) => w.value.id === chatId)
		.reduce(
			(
				chat: SceneChat | null,
				{ operation, value }: OptimisticWrite<SceneChat>
			): SceneChat | null => {
				switch (operation) {
					case 'insert':
						return value as SceneChat;
					case 'update':
						return { ...chat, ...value } as SceneChat;
					case 'delete':
						return null;
					default:
						return chat;
				}
			},
			sceneChat
		);
};

// Memoized: returns cached result when state and chatId haven't changed,
// preventing new object references from optimistic write spreads.
let _dsChatLastState: Store | null = null;
let _dsChatLastId: string | null | undefined = null;
let _dsChatLastResult: DsChat | null = null;

export const selectActiveDsChat = (state: Store, expectedChatId?: string | null): DsChat | null => {
	const chatId = expectedChatId ?? state.activeDsChat?.id ?? null;
	if (state === _dsChatLastState && chatId === _dsChatLastId) {
		return _dsChatLastResult;
	}
	_dsChatLastState = state;
	_dsChatLastId = chatId;
	if (!chatId) {
		_dsChatLastResult = null;
		return null;
	}

	const dsChat = state.activeDsChat?.id === chatId ? state.activeDsChat : null;
	const optimisticWrites = Object.values(state.optimisticDsChatWrites);

	_dsChatLastResult = optimisticWrites
		.filter((w) => w.value.id === chatId)
		.reduce((chat: DsChat | null, { operation, value }: OptimisticWrite<DsChat>): DsChat | null => {
			switch (operation) {
				case 'insert':
					return value as DsChat;
				case 'update':
					return { ...chat, ...value } as DsChat;
				case 'delete':
					return null;
				default:
					return chat;
			}
		}, dsChat);

	return _dsChatLastResult;
};

export const selectScenes = (state: Store): Scene[] => {
	const optimisticWrites = Object.values(state.optimisticSceneWrites);

	const scenes = optimisticWrites.reduce(
		(scenes: Scene[], { operation, value }: OptimisticWrite<Scene>): Scene[] => {
			switch (operation) {
				case 'insert':
					return [...scenes, value as Scene];
				case 'update':
					return scenes.map((scene) => (scene.id === value.id ? { ...scene, ...value } : scene));
				case 'delete':
					return scenes.filter((scene) => scene.id !== value.id);
				default:
					return scenes;
			}
		},
		state.scenes
	);

	// Filter out guest scenes that already exist in regular scenes to avoid duplicates
	const regularSceneIds = new Set(scenes.map((scene) => scene.id));
	const uniqueGuestScenes = state.guestScenes.filter(
		(guestScene) => !regularSceneIds.has(guestScene.id)
	);

	return [...scenes, ...uniqueGuestScenes];
};

export const selectGuestScenes = (state: Store): Scene[] => {
	const regularSceneIds = new Set(state.scenes.map((scene) => scene.id));
	return state.guestScenes.filter((guestScene) => !regularSceneIds.has(guestScene.id));
};

export const selectDesignSystems = (state: Store): DesignSystem[] => {
	const optimisticWrites = Object.values(state.optimisticDesignSystemWrites);

	const designSystems = optimisticWrites.reduce(
		(
			designSystems: DesignSystem[],
			{ operation, value }: OptimisticWrite<DesignSystem>
		): DesignSystem[] => {
			switch (operation) {
				case 'insert':
					return [...designSystems, value as DesignSystem];
				case 'update':
					return designSystems.map((ds) => (ds.id === value.id ? { ...ds, ...value } : ds));
				case 'delete':
					return designSystems.filter((ds) => ds.id !== value.id);
				default:
					return designSystems;
			}
		},
		state.designSystems
	);

	// Merge guest design systems, deduped
	const regularDsIds = new Set(designSystems.map((ds) => ds.id));
	const uniqueGuestDs = state.guestDesignSystems.filter((gds) => !regularDsIds.has(gds.id));

	return [...designSystems, ...uniqueGuestDs];
};

// returns only guest design systems that aren't already in the user's own list
export const selectGuestDesignSystems = (state: Store): DesignSystem[] => {
	const regularDsIds = new Set(state.designSystems.map((ds) => ds.id));
	return state.guestDesignSystems.filter((gds) => !regularDsIds.has(gds.id));
};

export const selectFrames = (state: Store, includeDeleted: boolean = false): Frame[] => {
	const optimisticWrites = Object.values(state.optimisticFrameWrites);
	const frames = optimisticWrites.reduce(
		(frames: Frame[], { operation, value }: OptimisticWrite<Frame>): Frame[] => {
			switch (operation) {
				case 'insert':
					return [...frames, value as Frame];
				case 'update':
					return frames.map((frame) => (frame.id === value.id ? { ...frame, ...value } : frame));
				case 'delete':
					return frames.filter((frame) => includeDeleted || frame.id !== value.id);
				default:
					return frames;
			}
		},
		state.frames
	);

	if (!includeDeleted) {
		return frames.filter((frame) => !frame.deletedAt);
	}

	// add the guest frames to the list
	return [...frames, ...state.guestFrames];
};

export const selectFrame = (state: Store, frameId?: string): Frame | null => {
	if (!frameId) return null;

	// mergeOptimisticWrites collapses 'update' writes per entity id, but inserts and
	// deletes remain as separate entries so we still reduce over all writes.
	const baseFrame = state.frames.find((f) => f.id === frameId) ?? null;
	const frame = state.optimisticFrameWrites.reduce<Frame | null>((current, write) => {
		if (write.value.id !== frameId) return current;
		switch (write.operation) {
			case 'insert':
				return write.value as Frame;
			case 'update':
				return current ? ({ ...current, ...write.value } as Frame) : current;
			case 'delete':
				return null;
			default:
				return current;
		}
	}, baseFrame);

	if (frame) return frame;

	const guestFrame = state.guestFrames.find((g) => g.id === frameId);
	return guestFrame ?? null;
};

export const selectDsChatRefs = (state: Store): ChatRef[] => {
	return state.dsChatRefs;
};

export const selectSceneChatRefs = (state: Store): ChatRef[] => {
	return state.sceneChatRefs;
};

// Memoized: returns stable reference when inputs (sceneComments, optimisticSceneCommentWrites) unchanged
let _prevSceneComments: SceneComment[] = [];
let _prevOptimisticCommentWrites: OptimisticWrite<SceneComment>[] = [];
let _cachedSceneComments: SceneComment[] = [];

export const selectSceneComments = (state: Store): SceneComment[] => {
	if (
		state.sceneComments === _prevSceneComments &&
		state.optimisticSceneCommentWrites === _prevOptimisticCommentWrites
	) {
		return _cachedSceneComments;
	}
	_prevSceneComments = state.sceneComments;
	_prevOptimisticCommentWrites = state.optimisticSceneCommentWrites;

	const optimisticWrites = Object.values(state.optimisticSceneCommentWrites);
	const merged = optimisticWrites.reduce(
		(
			comments: SceneComment[],
			{ operation, value }: OptimisticWrite<SceneComment>
		): SceneComment[] => {
			switch (operation) {
				case 'insert':
					return [...comments, value as SceneComment];
				case 'update':
					return comments.map((c) => {
						if (c.id !== value.id) return c;
						const merged = { ...c, ...value };
						if (value.metadata && c.metadata) {
							merged.metadata = { ...c.metadata, ...value.metadata };
						}
						return merged;
					});
				case 'delete':
					return comments.filter((c) => c.id !== value.id);
				default:
					return comments;
			}
		},
		state.sceneComments
	);
	_cachedSceneComments = [...merged].sort((a, b) => {
		const aOrder = a.metadata?.sortOrder ?? '';
		const bOrder = b.metadata?.sortOrder ?? '';
		return aOrder < bOrder ? -1 : aOrder > bOrder ? 1 : 0;
	});
	return _cachedSceneComments;
};

export const selectUser = (state: Store): User | null => {
	const optimisticWrites = Object.values(state.optimisticUserWrites);

	if (optimisticWrites.length === 0) {
		return state.user;
	}

	return optimisticWrites.reduce(
		(user: User | null, { operation, value }: OptimisticWrite<User>): User | null => {
			switch (operation) {
				case 'insert':
					return value as User;
				case 'update':
					// If user is null, we can't update it, so return null
					// If user exists, merge the partial update
					return user ? ({ ...user, ...value } as User) : null;
				case 'delete':
					return null;
				default:
					return user;
			}
		},
		state.user ?? null
	);
};

export const selectFrameCommentAuthor = (state: Store, userId: string): User | null => {
	return state.frameCommentAuthors.find((user) => user.id === userId) || null;
};

export const selectUserSettings = (state: Store): Required<UserSettings> => {
	const user = selectUser(state);

	// Guest users or no user: return defaults
	if (!user || user.id === 'guest') {
		return DEFAULT_USER_SETTINGS;
	}

	// Merge account settings with defaults
	return {
		...DEFAULT_USER_SETTINGS,
		...user.metadata?.settings,
	};
};

export const selectCreditWallet = (state: Store): CreditWallet | null => {
	return state.creditWallet;
};

export const selectIsGuest = (state: Store): boolean => {
	const user = selectUser(state);
	return user?.id === 'guest';
};

export const selectIsFreeTier = (state: Store): boolean => {
	return (state.subscription?.tier ?? Tier.FREE) === Tier.FREE;
};

export const selectOrganizations = (state: Store): OrganizationWithRole[] => {
	return state.organizations;
};

export const selectOrganizationSceneIds = (state: Store): Record<string, string[]> => {
	return state.organizationSceneIds;
};

export const selectOrganizationDesignSystemIds = (state: Store): Record<string, string[]> => {
	return state.organizationDesignSystemIds;
};

export const selectOrganizationCreditWallets = (
	state: Store
): Record<string, OrganizationCreditWallet> => {
	return state.organizationCreditWallets;
};

export const selectUserSceneIds = (state: Store): string[] => {
	return state.userSceneIds;
};

export const selectUserDesignSystemIds = (state: Store): string[] => {
	return state.userDesignSystemIds;
};

export const selectFigmaConnected = (state: Store): boolean => {
	return state.figmaConnected;
};

export const selectGithubConnected = (state: Store): boolean => {
	return state.githubConnected;
};

export const selectReferralInfo = (state: Store): ReferralInfo | null => {
	return state.referralInfo;
};

// Memoized: returns cached result when state and id haven't changed,
// preventing new object references from optimistic write spreads.
let _dsLastState: Store | null = null;
let _dsLastId: string | null = null;
let _dsLastResult: DesignSystem | null = null;

export const selectActiveDesignSystem = (
	state: Store,
	activeDesignSystemId: string | null
): DesignSystem | null => {
	if (state === _dsLastState && activeDesignSystemId === _dsLastId) {
		return _dsLastResult;
	}
	_dsLastState = state;
	_dsLastId = activeDesignSystemId;

	if (!activeDesignSystemId) {
		_dsLastResult = null;
		return null;
	}

	const designSystem =
		state.designSystems.find((ds) => ds.id === activeDesignSystemId) ||
		state.guestDesignSystems.find((ds) => ds.id === activeDesignSystemId) ||
		null;
	const optimisticWrites = Object.values(state.optimisticDesignSystemWrites);

	_dsLastResult = optimisticWrites
		.filter((w) => w.value.id === activeDesignSystemId)
		.reduce(
			(
				ds: DesignSystem | null,
				{ operation, value }: OptimisticWrite<DesignSystem>
			): DesignSystem | null => {
				switch (operation) {
					case 'insert':
						return value as DesignSystem;
					case 'update':
						return { ...ds, ...value } as DesignSystem;
					case 'delete':
						return null;
					default:
						return ds;
				}
			},
			designSystem
		);

	return _dsLastResult;
};

export const selectActiveDesignSystemVersion = (state: Store): DesignSystemVersion | null => {
	return state.activeDesignSystemVersion;
};

export const selectActiveSceneDsLink = (state: Store): SceneDsLink | null => {
	return state.activeSceneDsLink;
};

// returns true if the scene is view-only (guest access, not an editor)
export const selectIsSceneViewOnly = (state: Store, sceneId: string | null): boolean => {
	if (!sceneId) return true;
	return !state.scenes.some((s) => s.id === sceneId);
};

// returns true if the frame is view-only (its parent scene is not in the user's scene list)
export const selectIsFrameViewOnly = (state: Store, frameId: string | null): boolean => {
	if (!frameId) return true;
	const frame =
		state.frames.find((f) => f.id === frameId) || state.guestFrames.find((f) => f.id === frameId);
	if (!frame) return true;
	return !state.scenes.some((s) => s.id === frame.sceneId);
};

export default reducer;
